import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getFirestore, connectFirestoreEmulator, collection, getDocs, doc, getDoc, initializeFirestore, query, where,getCountFromServer } from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { db } from '../../firebase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/alert';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { getApp } from 'firebase/app';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Helper function to replace placeholders (client-side)
function replacePlaceholders(templateString, variables) {
  if (!templateString) return '';
  let resultString = templateString;
  for (const key in variables) {
    if (Object.prototype.hasOwnProperty.call(variables, key)) {
      const regex = new RegExp(`{{${key}}}`, 'g');
      resultString = resultString.replace(regex, variables[key] || '');
    }
  }
  return resultString;
}

const EmailAdminPage = () => {
  const [useEmulator, setUseEmulator] = useState(false);
  const [emulatorDb, setEmulatorDb] = useState(null);
  const [emulatorApp, setEmulatorApp] = useState(null);
  const [currentDb, setCurrentDb] = useState(db);
  const [functionsInstance, setFunctionsInstance] = useState(() => getFunctions(getApps()[0]));

  // State for form fields
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [customHtml, setCustomHtml] = useState('<h1>Hello {{userName}}!</h1><p>This is a test email from Coooly. Your main content goes here.</p>');
  const [singleTestUsesComposedContent, setSingleTestUsesComposedContent] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('Important Update from Coooly');
  const [templateVariables, setTemplateVariables] = useState({});
  
  // NEW: Editor state
  const [editorMode, setEditorMode] = useState('wysiwyg'); // 'wysiwyg' or 'html'
  
  // UI state
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Mass email state
  const [selectedEmailType, setSelectedEmailType] = useState('customAnnouncement');
  const [massSending, setMassSending] = useState(false);
  const [massSendResult, setMassSendResult] = useState(null);
  const [massTestRecipientEmail, setMassTestRecipientEmail] = useState('');
  const [sendingMassTest, setSendingMassTest] = useState(false);

  // Reminder testing state
  const [reminderTestResult, setReminderTestResult] = useState(null);
  const [testingReminders, setTestingReminders] = useState(false);
  const [reminderCounts, setReminderCounts] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [enableDetailedReminderDebug, setEnableDetailedReminderDebug] = useState(false);
  const [detailedReminderDebugInfo, setDetailedReminderDebugInfo] = useState(null);
  const [directDbCount, setDirectDbCount] = useState(null);
  const [loadingDirectDbCount, setLoadingDirectDbCount] = useState(false);

  // Inline message state
  const [errors, setErrors] = useState([]);
  const [successMessages, setSuccessMessages] = useState([]);

  // State for preview context
  const [previewMode, setPreviewMode] = useState('massEmailSystem');

  // Utility functions
  const safeGetNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const num = parseInt(value);
        return isNaN(num) ? 0 : num;
    }
    if (typeof value === 'object' && value !== null && value.total !== undefined) {
        // Ensure value.total is also treated as a number
        if (typeof value.total === 'number') return value.total;
        if (typeof value.total === 'string') {
            const numTotal = parseInt(value.total);
            return isNaN(numTotal) ? 0 : numTotal;
        }
        return 0; // If value.total is an object or something else unexpected
    }
    return 0;
  };

  const safeRender = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const addError = (message) => {
    setErrors(prev => [...prev, { id: Date.now(), message }]);
    setTimeout(() => setErrors(prev => prev.slice(1)), 5000);
  };

  const addSuccess = (message) => {
    setSuccessMessages(prev => [...prev, { id: Date.now(), message }]);
    setTimeout(() => setSuccessMessages(prev => prev.slice(1)), 5000);
  };

  // ReactQuill configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align',
    'link', 'image'
  ];

  // NEW: Generate preview function
  const generatePreview = () => {
    let htmlToPreview;
    let subjectToPreview = emailSubject;
    let recipientForPreview = recipientEmail || massTestRecipientEmail || 'user@example.com';
    let nameForPreview = recipientName || (recipientForPreview ? recipientForPreview.split('@')[0] : '') || 'User';

    // Default content if no specific mode matches or template is missing
    let baseHtmlForProcessing = '<p>No content to preview for current selection.</p>';

    if (previewMode === 'massEmailSystem') {
        const systemTemplate = templates.find(t => t.id === 'system');
        if (systemTemplate && systemTemplate.html) {
            // Inject editor content (customHtml) into the {{emailBody}} or {{{emailBody}}} of the system template
            baseHtmlForProcessing = systemTemplate.html.replace(/{{{?\s*emailBody\s*}}}?/g, customHtml);
        } else {
            // Fallback: if system template isn't found, preview the customHtml directly
            baseHtmlForProcessing = customHtml; 
            console.warn("System template not found for preview. Previewing editor content directly.");
        }
        subjectToPreview = emailSubject; // Subject is from the main subject input
    } else if (previewMode === 'singleTestComposed') {
        baseHtmlForProcessing = customHtml;
        subjectToPreview = emailSubject;
    } else if (previewMode === 'singleTestTemplate' && selectedTemplate && selectedTemplate.html) {
        baseHtmlForProcessing = selectedTemplate.html;
        subjectToPreview = selectedTemplate.subject || emailSubject;
    }
    
    const variables = {
      userName: nameForPreview,
      userEmail: recipientForPreview,
      emailSubject: subjectToPreview, 
      currentYear: new Date().getFullYear().toString(),
      // emailBody is effectively handled for massEmailSystem mode by injecting customHtml into the system template.
      // For other modes, if they use {{emailBody}}, it won't be filled unless explicitly in templateVariables.
      ...templateVariables 
    };
    
    let tempHtml = baseHtmlForProcessing;
    Object.entries(variables).forEach(([key, value]) => {
      if (value === undefined) return;
      // Ensure global replacement and case-insensitivity for placeholder keys, but preserve case of value
      const regex = new RegExp(`{{${key}}}`, 'gi'); // 'g' for global, 'i' for case-insensitive key matching
      tempHtml = tempHtml.replace(regex, String(value || '')); // Ensure value is a string
    });
    
    setPreviewHtml(tempHtml);
  };

  // Initialize emulator app and Firestore instance (runs once)
  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      try {
        const emulatorAppConfig = {
          apiKey: "demo-key",
          authDomain: "demo-project.firebaseapp.com",
          projectId: "demo-project",
          storageBucket: "demo-project.appspot.com",
          messagingSenderId: "123456789",
          appId: "demo-app-id"
        };
        
        // Ensure only one emulator-app instance is created
        let emuApp = getApps().find(app => app.name === 'emulator-app');
        if (!emuApp) {
          emuApp = initializeApp(emulatorAppConfig, 'emulator-app');
          console.log('Initialized dedicated emulator Firebase app instance.');
        }
        setEmulatorApp(emuApp); // Store the emulator app
        
        const emuDb = initializeFirestore(emuApp, {
          host: 'localhost:8080',
          ssl: false
        });
        setEmulatorDb(emuDb);
        console.log('Emulator Firestore instance ready for toggling.');

      } catch (error) {
        console.warn('Emulator app/Firestore setup warning:', error);
        addError('Failed to initialize emulator app/Firestore. Emulator mode may not work correctly.');
      }
    }
  }, []);

  // Update currentDb and functionsInstance when useEmulator toggle changes
  useEffect(() => {
    const defaultApp = getApps().find(app => app.name === '[DEFAULT]') || getApps()[0]; // Get the default (production) app

    if (useEmulator && emulatorApp && emulatorDb) {
      setCurrentDb(emulatorDb);
      const emuFunctions = getFunctions(emulatorApp); // Get Functions from the DEDICATED emulator app
      try {
        connectFunctionsEmulator(emuFunctions, 'localhost', 5001);
        setFunctionsInstance(emuFunctions);
        console.log('Switched to EMULATOR mode (Firestore & Functions from emulator-app).');
        addSuccess('🔧 Emulator Mode Active: Using local emulator for Firestore and Cloud Functions.');
      } catch (e) {
        if (e.message.includes("already connected")) {
          setFunctionsInstance(emuFunctions);
          console.log('Functions emulator (emulator-app) was already connected.');
          addSuccess('🔧 Emulator Mode Active (Functions already connected).');
        } else {
          console.error('Failed to connect functions emulator to emulator-app:', e);
          // Fallback to production functions if connecting emulator functions fails
          setFunctionsInstance(getFunctions(defaultApp));
          addError('Emulator Connection Issue: Failed to connect to Functions emulator. Using production Functions.');
        }
      }
    } else {
      setCurrentDb(db); // db is production Firestore from default app
      setFunctionsInstance(getFunctions(defaultApp)); // Use production Functions from default app
      console.log('Switched to PRODUCTION mode (Firestore & Functions from default app).');
      if (emulatorApp) { // Only show toast if switched by user, not on initial load without emulator setup
        addSuccess('🌐 Production Mode Active: Using live production data for Firestore and Cloud Functions.');
      }
    }
    setDirectDbCount(null); 
  }, [useEmulator, emulatorApp, emulatorDb]); // emulatorApp dependency added

  // Fetch templates from current database
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templatesSnapshot = await getDocs(collection(currentDb, 'emailTemplates'));
        const templatesList = templatesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTemplates(templatesList);
      } catch (error) {
        console.error('Error fetching templates:', error);
        addError('Failed to load email templates');
      }
    };
    
    fetchTemplates();
  }, [currentDb]);

  // Generate preview HTML when relevant states change
  useEffect(() => {
    generatePreview();
  }, [
    customHtml, 
    emailSubject, 
    recipientEmail, 
    recipientName, 
    massTestRecipientEmail, 
    selectedTemplate, 
    templateVariables,
    previewMode, // Added
    templates    // Added: to ensure preview updates if system template becomes available/changes
  ]);

  // Send test email
  const sendTestEmail = async () => {
    if (!recipientEmail) {
      addError('Please enter a recipient email address for the test email');
      return;
    }
    
    try {
      setSending(true);
      
      const data = {
        email: recipientEmail,
        name: recipientName || recipientEmail.split('@')[0] || 'Test User',
        variables: { 
          emailSubject: emailSubject,
          userName: recipientName || recipientEmail.split('@')[0] || 'Test User',
          userEmail: recipientEmail,
          ...templateVariables,
        },
      };

      if (singleTestUsesComposedContent) {
        data.customHtml = customHtml;
      } else {
        data.templateId = selectedTemplateId;
      }
      
      console.log('Sending single test email with data:', data);
      
      const sendEmail = httpsCallable(functionsInstance, 'sendEmailMessage');
      const result = await sendEmail(data);
      
      console.log('Email sent:', result.data);
      
      addSuccess(`Test email sent to ${recipientEmail}. Message ID: ${safeRender(result.data.messageId)}`);
    } catch (error) {
      console.error('Error sending test email:', error);
      addError(error.message || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  // NEW: Function to send a test of the mass email
  const sendTestMassEmail = async () => {
    if (!massTestRecipientEmail) {
      addError('Please enter a recipient email for the mass email test.');
      return;
    }
    if (!emailSubject || !customHtml) {
      addError('Please provide a subject and email body content for the test.');
      return;
    }

    const systemTemplate = templates.find(t => t.id === 'system');
    if (!systemTemplate || !systemTemplate.html) {
      addError('The "system" email template could not be found or is missing HTML. Please ensure it exists in Firestore.');
      return;
    }

    try {
      setSendingMassTest(true);
      const testRecipientName = massTestRecipientEmail.split('@')[0] || 'Test User';
      const currentYear = new Date().getFullYear().toString();

      // Variables for placeholders
      const userSpecificVariables = {
        userName: testRecipientName,
        userEmail: massTestRecipientEmail,
        currentYear: currentYear,
        // Add any other variables that admin might use in htmlBody or system template shell
      };

      // Stage 1: Process placeholders within the admin-provided customHtml (emailBody)
      const processedAdminHtmlBody = replacePlaceholders(customHtml, userSpecificVariables);
      
      // Unsubscribe link (simplified for client-side test, actual link generation is backend's concern for real sends)
      // For testing, we can assume it might be part of the system template or added by admin.
      // If the system template itself has {{unsubscribeLinkHtml}}, it would be replaced here if provided.
      const unsubscribeLinkHtml = `<p style="font-size:0.8em; text-align:center;"><a href="#unsubscribe">Test Unsubscribe</a></p>`;


      // Stage 2: Prepare variables for the system template shell and inject processed admin content
      const systemShellVars = {
        ...userSpecificVariables,
        emailBody: processedAdminHtmlBody,
        unsubscribeLinkHtml: unsubscribeLinkHtml, // Example, adjust if your system template uses this
      };
      
      const finalHtmlToSendForTest = replacePlaceholders(systemTemplate.html, systemShellVars);

      const data = {
        email: massTestRecipientEmail,
        name: testRecipientName,
        // Send fully processed HTML instead of templateId: 'system'
        customHtml: finalHtmlToSendForTest, 
        variables: {
          emailSubject: emailSubject, // This will be used as the email's subject line
          // Pass other variables for completeness, though HTML is pre-rendered
          userName: testRecipientName, 
          userEmail: massTestRecipientEmail, 
          currentYear: currentYear,
        },
      };

      console.log('Sending test of mass email with data (client-side prepared HTML):', data);
      const sendEmail = httpsCallable(functionsInstance, 'sendEmailMessage');
      const result = await sendEmail(data);

      console.log('Test of mass email sent:', result.data);
      addSuccess(`Test of mass email sent to ${massTestRecipientEmail}. Message ID: ${safeRender(result.data.messageId)}`);

    } catch (error) {
      console.error('Error sending test of mass email:', error);
      addError(error.message || 'Failed to send test of mass email.');
    } finally {
      setSendingMassTest(false);
    }
  };

  // NEW: Function to send mass emails
  const sendMassEmail = async () => {
    if (!emailSubject || !customHtml) {
      addError('Please provide a subject and email body content for the mass email.');
      return;
    }
    if (!selectedEmailType) {
      addError('Please select an email type for the mass email.');
      return;
    }

    try {
      setMassSending(true);
      setMassSendResult(null);

      const data = {
        subject: emailSubject, // Use subject from main input
        htmlBody: customHtml,  // Use body content from main input
        emailType: selectedEmailType,
      };

      console.log('Sending mass email with data:', data);
      const callSendAdminEmail = httpsCallable(functionsInstance, 'sendAdminEmail');
      const result = await callSendAdminEmail(data);

      console.log('Mass email send result:', result.data);
      setMassSendResult(result.data);

      if (result.data.success) {
        addSuccess(`Mass email process initiated for type "${selectedEmailType}". Sent: ${result.data.emailsSent}, Failed: ${result.data.emailsFailed}.`);
      } else {
        addError(result.data.message || `Failed to send mass emails for type "${selectedEmailType}".`);
      }

    } catch (error) {
      console.error('Error sending mass email:', error);
      addError(error.message || 'Failed to send mass email.');
      setMassSendResult({ success: false, message: error.message, emailsSent: 0, emailsFailed: 0 });
    } finally {
      setMassSending(false);
    }
  };
  
  // Modify the template selection handler
  const handleTemplateSelect = async (templateId) => {
    setSelectedTemplateId(templateId);
    
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }
    
    try {
      const templateDoc = await getDoc(doc(currentDb, 'emailTemplates', templateId));
      if (templateDoc.exists()) {
        setSelectedTemplate(templateDoc.data());
      } else {
        console.error(`Template not found: ${templateId}`);
        setSelectedTemplate(null);
        addError(`The template with ID "${templateId}" could not be found.`);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      setSelectedTemplate(null);
      addError('Failed to load template details');
    }
  };
  
  // Update preview mode when relevant states change
  useEffect(() => {
    if (massSending || sending) return; // Don't change preview mode during send operations

    if (!singleTestUsesComposedContent && selectedTemplateId && selectedTemplate) {
      // User is specifically testing a selected template for a single email
      setPreviewMode('singleTestTemplate');
    } else if (singleTestUsesComposedContent && (recipientEmail || (massTestRecipientEmail && editorMode === 'html'))) {
      // User is composing a single test email with custom HTML (not a template)
      // or testing the mass email with a specific recipient.
      setPreviewMode('singleTestComposed');
    }
    else {
      // Default to previewing how the composed content would look in the mass email system
      // This assumes the main editor is primarily for mass email content.
      setPreviewMode('massEmailSystem');
    }
  }, [
    singleTestUsesComposedContent, 
    selectedTemplateId, 
    selectedTemplate, 
    recipientEmail, 
    massTestRecipientEmail,
    massSending,
    sending,
    editorMode // Added editorMode as it might influence context for massTestRecipientEmail
  ]);

  // Test GDC/Insurance reminders
  const testGdcInsuranceReminders = async () => {
    try {
      setTestingReminders(true);
      setReminderTestResult(null);
      
      console.log(`IMPORTANT: Calling 'sendGdcInsuranceReminders' (mode: send) using ${useEmulator ? 'EMULATOR' : 'PRODUCTION'} functions.`);
      // For this test, we assume it's a general trigger, not specific to 'send-scheduled' mode.
      // The backend function 'sendGdcInsuranceReminders' should handle its default send mode.
      const sendRemindersCallable = httpsCallable(functionsInstance, 'sendGdcInsuranceReminders');
      const result = await sendRemindersCallable({}); // Empty data for default send operation
      
      const reminderData = result.data;
      setReminderTestResult(reminderData);
      
      const gdcCount = safeGetNumber(reminderData.gdcReminders);
      const insuranceCount = safeGetNumber(reminderData.insuranceReminders);
      
      addSuccess(`Reminders Processed - GDC: ${gdcCount}, Insurance: ${insuranceCount}`);
    } catch (error) {
      console.error('Error testing reminders:', error);
      addError(error.message || 'Failed to process reminders');
      setReminderTestResult({ error: error.message });
    } finally {
      setTestingReminders(false);
    }
  };
  
  // Check reminder counts
  const checkReminderCounts = async () => {
    try {
      setLoadingCounts(true);
      setReminderCounts(null);
      setDetailedReminderDebugInfo(null); 
      
      const requestData = { 
        mode: 'count', 
        countOnly: true,
        detailedDebug: enableDetailedReminderDebug,
        applyDayRangeThreshold: enableDetailedReminderDebug, 
      };
      
      console.log(`Checking reminder counts with data (using ${useEmulator ? 'EMULATOR' : 'PRODUCTION'} functions):`, requestData);
      
      const getReminderCountsCallable = httpsCallable(functionsInstance, 'sendGdcInsuranceReminders');
      const result = await getReminderCountsCallable(requestData);
      
      const responseData = result.data;
      console.log("Count data received:", responseData);
      
      const countsToSet = {
        totalPending: 0,
        totalSuccess: 0, // For count mode, success might mean "would be sent"
        totalErrors: safeGetNumber(responseData.errors),
        usersProcessed: safeGetNumber(responseData.usersProcessed),
        mode: responseData.mode,
        isEmulatedRun: responseData.isEmulatedRun,
        projectId: responseData.projectId,
        environment: responseData.environment,
        // Add any other relevant fields from responseData you want to store/display
      };

      if (responseData.mode && responseData.mode.toUpperCase().includes('COUNT')) {
        countsToSet.totalPending = safeGetNumber(responseData.emailsSent); // In count mode, emailsSent is "would be sent"
        // countsToSet.totalSuccess = safeGetNumber(responseData.emailsSent); // Or interpret as potential successes
      } else { // Assuming 'SEND' mode
        countsToSet.totalSuccess = safeGetNumber(responseData.emailsSent);
      }
      setReminderCounts(countsToSet);


      if (enableDetailedReminderDebug) {
        if (responseData.debugLog && Array.isArray(responseData.debugLog)) {
          setDetailedReminderDebugInfo(responseData.debugLog);
          if (responseData.debugLog.length > 0) {
            addSuccess(`Found ${responseData.debugLog.length} user(s) in debug log.`);
          } else {
            addSuccess('No users were processed or matched the initial criteria for detailed logging.');
          }
        } else {
          setDetailedReminderDebugInfo([]);
          addError('Detailed debug was enabled, but the Cloud Function did not return the expected debugLog array.');
        }
      }

      if (!enableDetailedReminderDebug) {
        const pendingCount = countsToSet.totalPending;
        if (pendingCount === 0 && countsToSet.mode && countsToSet.mode.toUpperCase().includes('COUNT')) {
          addSuccess('No users currently meet the criteria for reminders.');
        } else if (countsToSet.mode && countsToSet.mode.toUpperCase().includes('COUNT')) {
          addSuccess(`${pendingCount} reminders are ready to send`);
        }
      }
      
    } catch (error) {
      console.error('Error checking reminder counts:', error);
      addError(error.message || 'Failed to check reminder counts');
      setReminderCounts(null);
      setDetailedReminderDebugInfo(null);
    } finally {
      setLoadingCounts(false);
    }
  };

  // Add a new function that creates a completely fresh Firebase app instance for production
  const checkReminderCountsProduction = async () => {
    try {
      setLoadingCounts(true);
      setReminderCounts(null);
      setDetailedReminderDebugInfo(null);
      
      // Create a completely fresh Firebase app instance specifically for production
      // IMPORTANT: Replace with your actual production Firebase config
      const productionConfig = {
        apiKey: "YOUR_PRODUCTION_API_KEY", 
        authDomain: "YOUR_PRODUCTION_AUTH_DOMAIN", 
        projectId: "YOUR_PRODUCTION_PROJECT_ID", 
        storageBucket: "YOUR_PRODUCTION_STORAGE_BUCKET", 
        messagingSenderId: "YOUR_PRODUCTION_MESSAGING_SENDER_ID", 
        appId: "YOUR_PRODUCTION_APP_ID" 
      };
      
      let tempProductionApp = getApps().find(app => app.name === 'temp-production-check');
      if (tempProductionApp) {
        // If it somehow exists, delete and re-initialize to ensure it's clean
        await tempProductionApp.delete();
        tempProductionApp = initializeApp(productionConfig, 'temp-production-check');
      } else {
        tempProductionApp = initializeApp(productionConfig, 'temp-production-check');
      }
      
      const productionFunctions = getFunctions(tempProductionApp);
      
      console.log(`CREATING FRESH PRODUCTION APP:`, {
        projectId: tempProductionApp.options.projectId,
        appName: tempProductionApp.name
      });
      
      const requestData = { 
        mode: 'count', 
        countOnly: true,
        detailedDebug: enableDetailedReminderDebug,
        applyDayRangeThreshold: enableDetailedReminderDebug, 
      };
      
      console.log(`FORCE PRODUCTION (Fresh App): Checking reminder counts:`, requestData);
      
      const getReminderCounts = httpsCallable(productionFunctions, 'sendGdcInsuranceReminders');
      const result = await getReminderCounts(requestData);
      
      const responseData = result.data;
      console.log("FRESH PRODUCTION Count data received:", responseData);
      
      // Check if it's actually production
      if (responseData.isEmulatedRun === true) {
        addError(`Still Using Emulator! Project: ${safeRender(responseData.projectId)}. Check your Firebase config - may be pointing to wrong project.`);
      } else {
        addSuccess(`Production Function Called Successfully! Project: ${safeRender(responseData.projectId)}, Environment: ${safeRender(responseData.environment)}`);
      }

      if (responseData && typeof responseData.totalPending !== 'undefined') {
        setReminderCounts(responseData);
      }

      if (enableDetailedReminderDebug && responseData.debugLog) {
        setDetailedReminderDebugInfo(responseData.debugLog);
      }
      
      // Clean up the temporary app
      try {
        await tempProductionApp.delete();
      } catch (deleteError) {
        console.warn('Could not delete temporary app:', deleteError);
      }
      
    } catch (error) {
      console.error('Error in FRESH PRODUCTION check:', error);
      addError('Production Test Failed: ' + error.message);
    } finally {
      setLoadingCounts(false);
    }
  };

  // Add a completely isolated production test
  const testDirectProduction = async () => {
    try {
      setLoadingCounts(true);
      setReminderCounts(null);
      setDetailedReminderDebugInfo(null);
      
      console.log('=== DIRECT PRODUCTION TEST (Using Default App) ===');
      
      const defaultApp = getApps().find(app => app.name === '[DEFAULT]') || getApps()[0];
      if (!defaultApp) {
        addError("Default Firebase app not found. Cannot run production test.");
        setLoadingCounts(false);
        return;
      }
      console.log('Default app config:', {
        projectId: defaultApp.options.projectId,
        authDomain: defaultApp.options.authDomain,
        appName: defaultApp.name
      });
      
      const productionFunctions = getFunctions(defaultApp); 

      console.log(`Using functionsInstance for production test. Current functionsInstance app name: ${functionsInstance.app.name}`);
      
      const requestData = { 
        mode: 'count', 
        countOnly: true,
        detailedDebug: enableDetailedReminderDebug,
        applyDayRangeThreshold: enableDetailedReminderDebug, 
      };
      
      console.log('Calling production function with data:', requestData);
      
      const getReminderCountsCallable = httpsCallable(productionFunctions, 'sendGdcInsuranceReminders');
      const result = await getReminderCountsCallable(requestData);
      
      const responseData = result.data;
      console.log('=== PRODUCTION RESPONSE ===', responseData);
      
      const consentingUsersCount = (responseData.consentingUsers && typeof responseData.consentingUsers === 'object') 
        ? responseData.consentingUsers.total || 0
        : responseData.consentingUsers || 0;
      
      if (responseData.environment === 'production' || responseData.isEmulatedRun === false) {
        addSuccess(`🎉 Production Success! Environment: ${safeRender(responseData.environment)}, Project: ${safeRender(responseData.projectId)}, Consenting Users (from func): ${consentingUsersCount}`);
      } else {
        addError(`⚠️ Still Emulator - Environment: ${safeRender(responseData.environment)}, Project: ${safeRender(responseData.projectId)}`);
      }

      // Correctly map responseData to reminderCounts state
      const countsToSet = {
        totalPending: 0,
        totalSuccess: 0,
        totalErrors: safeGetNumber(responseData.errors),
        usersProcessed: safeGetNumber(responseData.usersProcessed),
        mode: responseData.mode,
        isEmulatedRun: responseData.isEmulatedRun,
        projectId: responseData.projectId,
        environment: responseData.environment,
        // Add any other relevant fields from responseData
      };

      if (responseData.mode && responseData.mode.toUpperCase().includes('COUNT')) {
        countsToSet.totalPending = safeGetNumber(responseData.emailsSent); // In count mode, emailsSent is "would be sent"
        // countsToSet.totalSuccess = safeGetNumber(responseData.emailsSent); // Or interpret as potential successes
      } else { // Assuming 'SEND' mode
        countsToSet.totalSuccess = safeGetNumber(responseData.emailsSent);
      }
      setReminderCounts(countsToSet);


      if (enableDetailedReminderDebug && responseData.debugLog) {
        setDetailedReminderDebugInfo(responseData.debugLog);
      }
      
    } catch (error) {
      console.error('=== PRODUCTION TEST ERROR ===', error);
      addError('Production Test Failed: ' + error.message);
    } finally {
      setLoadingCounts(false);
    }
  };

  // Direct Firestore count
  const getDirectFirestoreCount = async () => {
    setLoadingDirectDbCount(true);
    setDirectDbCount(null);
    try {
      const usersRef = collection(currentDb, 'users');
      const q = query(usersRef, 
        where('profileComplete', '==', true),
        where('consentPreferences.email.gdcInsuranceReminders', '==', true)
      );
      const snapshot = await getCountFromServer(q);
      const count = snapshot.data().count;
      setDirectDbCount(count);
      addSuccess(`Direct Firestore Count (${useEmulator ? 'Emulator' : 'Production'}): Found ${count} users matching criteria.`);
    } catch (error) {
      console.error('Error getting direct Firestore count:', error);
      addError('Direct Count Error: ' + error.message);
      setDirectDbCount('Error');
    } finally {
      setLoadingDirectDbCount(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Error and Success Messages */}
      {errors.length > 0 && (
        <div className="mb-4 space-y-2">
          {errors.map(error => (
            <Alert key={error.id} className="bg-red-50 border-red-200">
              <AlertTitle className="text-red-800">Error</AlertTitle>
              <AlertDescription className="text-red-700">{safeRender(error.message)}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}
      
      {successMessages.length > 0 && (
        <div className="mb-4 space-y-2">
          {successMessages.map(message => (
            <Alert key={message.id} className="bg-green-50 border-green-200">
              <AlertTitle className="text-green-800">Success</AlertTitle>
              <AlertDescription className="text-green-700">{safeRender(message.message)}</AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Email Administration</h1>
        
        <div className="flex items-center gap-4">
          {window.location.hostname === 'localhost' && (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setUseEmulator(!useEmulator)}
                variant={useEmulator ? "default" : "outline"}
                size="sm"
                className={useEmulator ? "bg-orange-600 hover:bg-orange-700" : ""}
              >
                {useEmulator ? "🔧 Emulator" : "🌐 Production"}
              </Button>
              <span className="text-xs text-gray-500">
                {useEmulator ? "Local Data" : "Live Data"}
              </span>
            </div>
          )}
          
          <Link 
            to="/admin/reminder-dashboard" 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            View Dashboard
          </Link>
        </div>
      </div>
      
      {/* Environment Indicator */}
      {window.location.hostname === 'localhost' && (
        <Alert className={`mb-6 ${useEmulator ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
          <AlertTitle className={useEmulator ? 'text-orange-800' : 'text-green-800'}>
            {useEmulator ? '🔧 Development Mode' : '🌐 Production Mode'}
          </AlertTitle>
          <AlertDescription className={useEmulator ? 'text-orange-700' : 'text-green-700'}>
            {useEmulator 
              ? 'Using local emulator data for testing. Templates and users from local Firestore.'
              : 'Using live production data. Changes will affect real templates and users.'
            }
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Email Composition and Sending Card */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>Compose & Send Email</CardTitle>
            <CardDescription>Create your email content below. Mass emails will use this content with the "system" template.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Email Subject */}
            <div>
              <Label htmlFor="emailSubject">Email Subject</Label>
              <Input
                id="emailSubject"
                placeholder="Email Subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
              />
            </div>

            {/* Email Body with Tabs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Email Body Content</Label>
                <div className="flex border rounded-md">
                  <Button
                    type="button"
                    variant={editorMode === 'wysiwyg' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-r-none"
                    onClick={() => setEditorMode('wysiwyg')}
                  >
                    Visual
                  </Button>
                  <Button
                    type="button"
                    variant={editorMode === 'html' ? 'default' : 'ghost'}
                    size="sm"
                    className="rounded-l-none"
                    onClick={() => setEditorMode('html')}
                  >
                    HTML
                  </Button>
                </div>
              </div>

              {editorMode === 'wysiwyg' ? (
                <div className="border rounded-md">
                  <ReactQuill
                    value={customHtml}
                    onChange={setCustomHtml}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ height: '200px', marginBottom: '42px' }}
                    placeholder="Enter your email content here..."
                    theme="snow"
                  />
                </div>
              ) : (
                <Textarea
                  className="font-mono text-sm dark:bg-gray-900 dark:text-gray-100 dark:border-gray-600"
                  rows={12}
                  value={customHtml}
                  onChange={(e) => setCustomHtml(e.target.value)}
                  placeholder="<h1>Hello {{userName}}!</h1><p>Your main HTML content goes here. This will be injected into the {{emailBody}} of the system template for mass emails.</p>"
                />
              )}
              
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                For mass emails, this content is placed into the standard "system" template.
                Placeholders like `{"{{userName}}"}`, `{"{{currentYear}}"}` will be automatically filled.
              </p>
            </div>

            {/* Mass Email Section */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-medium mb-2">Send Mass Email</h3>
              <p className="text-sm text-gray-600 mb-3">
                Uses the Subject and Body Content above with the "system" template.
                Recipients are determined by the selected Email Type and their consent preferences.
              </p>
              <div>
                <Label htmlFor="emailType">Email Type</Label>
                <select
                  id="emailType"
                  className="w-full p-2 border rounded mb-3 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                  value={selectedEmailType}
                  onChange={(e) => setSelectedEmailType(e.target.value)}
                >
                  <option value="customAnnouncement">Custom Announcement (to System Update Subscribers)</option>
                  <option value="systemMaintenance">System Maintenance</option>
                  <option value="marketing">Marketing</option>
                </select>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                <Button
                  onClick={sendMassEmail}
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  disabled={massSending || sendingMassTest}
                >
                  {massSending ? <LoadingSpinner size="sm" /> : `Send Mass Email (${selectedEmailType})`}
                </Button>
                <div className="flex gap-2">
                  <Input 
                    id="massTestRecipientEmail"
                    type="email"
                    placeholder="Test recipient for mass email"
                    value={massTestRecipientEmail}
                    onChange={(e) => setMassTestRecipientEmail(e.target.value)}
                    className="flex-grow"
                  />
                  <Button
                    onClick={sendTestMassEmail}
                    variant="outline"
                    disabled={massSending || sendingMassTest || !massTestRecipientEmail}
                  >
                    {sendingMassTest ? <LoadingSpinner size="sm" /> : 'Test'}
                  </Button>
                </div>
              </div>
              {massSendResult && (
                <div className={`mt-4 p-3 border rounded ${massSendResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className="font-medium text-sm">Mass Send Status:</p>
                  <p className="text-xs">Sent: {massSendResult.emailsSent}, Failed: {massSendResult.emailsFailed}</p>
                  {massSendResult.message && <p className="text-xs">Message: {massSendResult.message}</p>}
                  {massSendResult.failedDetails && massSendResult.failedDetails.length > 0 && (
                     <details className="text-xs mt-1">
                       <summary>Failed Email Details ({massSendResult.failedDetails.length})</summary>
                       <ul className="list-disc pl-4 max-h-20 overflow-y-auto">
                         {massSendResult.failedDetails.map((f, i) => <li key={i}>{f.email}: {f.error}</li>)}
                       </ul>
                     </details>
                  )}
                </div>
              )}
            </div>

            {/* Single Test Email Section */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-medium mb-2">Send Single Test Email</h3>
              <div className="grid grid-cols-1 gap-4 mb-3">
                <div>
                  <Label htmlFor="recipientEmail">Test Recipient Email*</Label>
                  <Input
                    id="recipientEmail"
                    type="email"
                    placeholder="recipient@example.com"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="recipientName">Test Recipient Name (Optional)</Label>
                  <Input
                    id="recipientName"
                    placeholder="John Doe"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <Button
                  onClick={() => setSingleTestUsesComposedContent(!singleTestUsesComposedContent)}
                  variant={singleTestUsesComposedContent ? "default" : "outline"}
                  className="w-full"
                >
                  {singleTestUsesComposedContent ? "Test with Composed Content Above" : "Test with Selected Template Below"}
                </Button>
              </div>

              {!singleTestUsesComposedContent && (
                <div className="space-y-3 p-3 border rounded bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600">
                  <div>
                    <Label htmlFor="templateId">Select Template for Test</Label>
                    <select
                      id="templateId"
                      className="w-full p-2 border rounded dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      value={selectedTemplateId}
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                    >
                      <option value="">-- Select a Template --</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {(template.name || template.id)} ({template.id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Custom Variables for Selected Template</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Variable name" className="text-sm dark:bg-gray-900 dark:border-gray-600" id="varName"/>
                      <Input placeholder="Value" className="text-sm dark:bg-gray-900 dark:border-gray-600" id="varValue"/>
                    </div>
                    <Button
                      className="mt-2 w-full" variant="outline" size="sm"
                      onClick={() => {
                        const name = document.getElementById('varName').value;
                        const value = document.getElementById('varValue').value;
                        if (name) {
                          setTemplateVariables({...templateVariables, [name]: value });
                          document.getElementById('varName').value = '';
                          document.getElementById('varValue').value = '';
                        }
                      }}
                    >Add Variable</Button>
                    {Object.keys(templateVariables).length > 0 && (
                      <div className="mt-2 p-2 border rounded bg-muted dark:bg-gray-700 dark:border-gray-600">
                        <p className="text-sm font-medium mb-1 dark:text-gray-200">Added Variables:</p>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                          {Object.entries(templateVariables).map(([key, value]) => (
                            <React.Fragment key={key}>
                              <div className="text-xs truncate dark:text-gray-300">{key}</div>
                              <div className="text-xs truncate dark:text-gray-300">{value}</div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <Button
                onClick={sendTestEmail}
                className="w-full mt-3"
                disabled={sending}
              >
                {sending ? <LoadingSpinner size="sm" /> : 'Send Single Test Email'}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Preview Card */}
        <Card className="h-fit">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Email Preview</CardTitle>
                <CardDescription>
                  {previewMode === 'massEmailSystem' && "Previewing composed content injected into the 'system' template."}
                  {previewMode === 'singleTestComposed' && "Previewing composed content for Single Test Email."}
                  {previewMode === 'singleTestTemplate' && `Previewing selected template: ${selectedTemplate?.name || selectedTemplateId || 'N/A'}.`}
                </CardDescription>
              </div>
              <Button 
                onClick={generatePreview}
                variant="outline"
                size="sm"
              >
                Refresh Preview
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* DEBUG: Log the HTML content being sent to the preview */}
            {/* You can check your browser's developer console to see this output */}
            {console.log('Current previewHtml:', previewHtml)}
            {previewHtml ? (
              <div className="border rounded p-4 bg-white dark:bg-gray-900 max-h-[600px] overflow-y-auto"> {/* Increased max-height */}
                <div className="mb-4 p-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <p className="font-medium text-gray-900 dark:text-gray-100">Subject: {emailSubject}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">To: {recipientEmail || massTestRecipientEmail || 'recipient@example.com'}</p>
                </div>
                <div
                  // Removed prose classes to allow raw HTML/template styles to dominate.
                  // Added basic text color for dark mode readability if not specified in email HTML.
                  className="text-gray-900 dark:text-gray-100" 
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <p>Click "Refresh Preview" to see how your email will look</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Enhanced GDC/Insurance Reminder Testing */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>GDC/Insurance Reminder Testing</CardTitle>
          <CardDescription>
            Test reminder functionality. Multiple methods to ensure production connectivity.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Environment Status Alert */}
            <Alert className={`${useEmulator ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
              <AlertTitle className={`${useEmulator ? 'text-red-800' : 'text-blue-800'}`}>Current Status</AlertTitle>
              <AlertDescription className={`${useEmulator ? 'text-red-700' : 'text-blue-700'}`}>
                Environment Toggle: {useEmulator ? 'Emulator' : 'Production'} | 
                Last Response: {reminderCounts?.isEmulatedRun === true ? 'Emulator' : reminderCounts?.isEmulatedRun === false ? 'Production' : 'Unknown'}
                {reminderCounts?.projectId && ` | Project: ${safeRender(reminderCounts.projectId)}`}
              </AlertDescription>
            </Alert>

            {/* Direct Firestore Count Button and Display */}
            <div className="mt-4 p-3 border rounded bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600">
              <h4 className="text-md font-semibold mb-2 dark:text-gray-200">Direct Firestore Query:</h4>
              <Button
                onClick={getDirectFirestoreCount}
                disabled={loadingDirectDbCount}
                variant="secondary"
                className="w-full mb-2"
              >
                {loadingDirectDbCount ? <LoadingSpinner size="sm" /> : `Count Users Directly from ${useEmulator ? 'Emulator DB' : 'Production DB'}`}
              </Button>
              {directDbCount !== null && (
                <p className="text-sm text-center dark:text-gray-300">
                  Direct Count Result: <span className="font-bold">{safeRender(directDbCount)}</span>
                </p>
              )}
            </div>
            
            {/* Debug checkbox */}
            <div className="flex items-center space-x-2 mt-4">
              <input
                type="checkbox"
                id="enableDetailedDebug"
                checked={enableDetailedReminderDebug}
                onChange={(e) => setEnableDetailedReminderDebug(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <Label htmlFor="enableDetailedDebug" className="text-sm font-medium">
                Enable Detailed Debug for "Check Pending" (includes +/-1 day range)
              </Label>
            </div>
            
            {/* Enhanced Button Grid */}
            <div className="grid grid-cols-1 gap-2">
              <Button 
                onClick={checkReminderCounts}
                disabled={loadingCounts}
                variant="outline"
                className="w-full"
              >
                {loadingCounts ? <LoadingSpinner size="sm" /> : `Check Pending (${useEmulator ? 'Emulator' : 'Production'})`}
              </Button>
              
              <Button 
                onClick={testDirectProduction}
                disabled={loadingCounts}
                variant="default"
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loadingCounts ? <LoadingSpinner size="sm" /> : 'DIRECT PRODUCTION TEST'}
              </Button>
              
              <Button 
                onClick={testGdcInsuranceReminders}
                disabled={testingReminders}
                variant="destructive"
                className="w-full"
              >
                {testingReminders ? <LoadingSpinner size="sm" /> : 'Send Reminders Now (CAUTION)'}
              </Button>
            </div>

            {/* Results display - All values safely rendered */}
            {reminderCounts && (
              <div className="mt-4 p-4 border rounded bg-muted dark:bg-gray-800 border-gray-200 dark:border-gray-600">
                <p className="text-sm font-medium mb-2 dark:text-gray-200">Reminder Counts:</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <p className="text-lg font-bold dark:text-gray-100">{safeGetNumber(reminderCounts.totalPending)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold dark:text-gray-100">{safeGetNumber(reminderCounts.totalSuccess)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Success</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold dark:text-gray-100">{safeGetNumber(reminderCounts.totalErrors)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Errors</p>
                  </div>
                </div>
                
                {/* Detailed debug info */}
                {enableDetailedReminderDebug && detailedReminderDebugInfo && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2 dark:text-gray-200">Detailed Debug Info:</p>
                    <div className="max-h-60 overflow-auto p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-600">
                      {detailedReminderDebugInfo.length === 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">No specific entries found for the selected user(s).</p>
                      ) : (
                        <ul className="list-disc list-inside space-y-1">
                          {detailedReminderDebugInfo.map((entry, index) => (
                            <li key={index} className="text-xs dark:text-gray-300">
                              {safeRender(entry)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Help Card */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Email Variables Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-sm">
            The "system" template (used for mass emails) and other templates can use these common variables:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm dark:text-gray-300">
            <li><code className="bg-muted dark:bg-gray-700 p-1 rounded">{"{{userName}}"}</code> - Recipient's name.</li>
            <li><code className="bg-muted dark:bg-gray-700 p-1 rounded">{"{{userEmail}}"}</code> - Recipient's email address.</li>
            <li><code className="bg-muted dark:bg-gray-700 p-1 rounded">{"{{currentYear}}"}</code> - The current year (e.g., for copyright).</li>
            <li><code className="bg-muted dark:bg-gray-700 p-1 rounded">{"{{emailBody}}"}</code> - (Specifically for "system" template) Where your main Email Body Content is injected.</li>
          </ul>
          <p className="mt-3 text-sm dark:text-gray-300">
            When testing other specific templates in the "Single Test Email" section, you can add more variables as needed by that template.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailAdminPage;
